import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, ArrowRight, Upload, Download, Save, FolderOpen, X, Sparkles, Search } from 'lucide-react';
import { ScanCreateGameDialog } from '@/components/stats/ScanCreateGameDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function downloadTemplate() {
  const csv = "jersey_number,player_name\n1,Player One\n2,Player Two\n3,Player Three\n";
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'team_roster_template.csv';
  a.click();
}

function parseCSV(text) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const players = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (i === 0 && (line.toLowerCase().includes('jersey') || line.toLowerCase().includes('player') || line.toLowerCase().includes('name'))) continue;
    const parts = line.split(',').map(s => s.trim().replace(/^"|"$/g, ''));
    if (parts.length >= 2 && parts[1]) {
      players.push({ jersey_number: parts[0], name: parts[1] });
    } else if (parts.length === 1 && parts[0]) {
      players.push({ jersey_number: '', name: parts[0] });
    }
  }
  return players;
}

// TeamForm extracted as a standalone component to prevent re-mount on parent state changes
function TeamForm({ team, onUpdateTeam, onAddPlayer, onRemovePlayer, onUpdatePlayer, onCSVImport, onSave, onLoadSaved, hasSavedTeams, label, teamKey }) {
  const fileRef = useRef(null);
  const [showBulk, setShowBulk] = useState(false);
  const bulkRef = useRef(null);

  const handleBulkAdd = () => {
    const text = bulkRef.current?.value || '';
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) { toast.error('No player names entered'); return; }
    const players = lines.map(line => {
      const parts = line.split(',').map(s => s.trim());
      if (parts.length >= 2) return { jersey_number: parts[0], name: parts[1] };
      return { jersey_number: '', name: parts[0] };
    });
    onUpdateTeam({ ...team, players });
    bulkRef.current.value = '';
    setShowBulk(false);
    toast.success(`Loaded ${players.length} player(s)`);
  };

  return (
    <Card className="flex-1">
      <CardContent className="p-5">
        <h3 className="text-lg font-bold text-[#00247D] mb-3 uppercase" style={{ fontFamily: 'Barlow Condensed' }}>{label}</h3>

        <Input
          placeholder="Team Name"
          value={team.name}
          onChange={(e) => onUpdateTeam({ ...team, name: e.target.value })}
          className="mb-3 font-semibold"
          data-testid={`team-${teamKey}-name`}
        />

        {/* Action buttons */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          <input type="file" accept=".csv" ref={fileRef} className="hidden"
            onChange={(e) => { onCSVImport(e); e.target.value = ''; }} />
          <Button variant="outline" size="sm" className="text-xs h-7"
            onClick={() => fileRef.current?.click()} data-testid={`import-csv-${teamKey}`}>
            <Upload className="w-3 h-3 mr-1" /> Import CSV
          </Button>
          <Button variant="outline" size="sm" className="text-xs h-7"
            onClick={downloadTemplate} data-testid={`download-template-${teamKey}`}>
            <Download className="w-3 h-3 mr-1" /> Template
          </Button>
          <Button variant="outline" size="sm" className="text-xs h-7"
            onClick={() => setShowBulk(!showBulk)} data-testid={`bulk-paste-toggle-${teamKey}`}>
            <Plus className="w-3 h-3 mr-1" /> Bulk Paste
          </Button>
          {hasSavedTeams && (
            <Button variant="outline" size="sm" className="text-xs h-7"
              onClick={onLoadSaved} data-testid={`load-saved-${teamKey}`}>
              <FolderOpen className="w-3 h-3 mr-1" /> Load Saved
            </Button>
          )}
          <Button variant="outline" size="sm" className="text-xs h-7 text-green-600 border-green-200 hover:bg-green-50"
            onClick={onSave} data-testid={`save-team-${teamKey}`}>
            <Save className="w-3 h-3 mr-1" /> Save Team
          </Button>
        </div>

        {/* Bulk Paste Area */}
        {showBulk && (
          <div className="mb-4 p-3 border border-slate-200 rounded-lg bg-slate-50">
            <p className="text-xs text-slate-500 mb-2">Paste player names, one per line. Format: <code className="bg-white px-1 rounded">jersey,name</code> or just <code className="bg-white px-1 rounded">name</code></p>
            <textarea
              ref={bulkRef}
              className="w-full h-24 p-2 text-sm border border-slate-200 rounded resize-none focus:outline-none focus:ring-2 focus:ring-[#00247D]/20 focus:border-[#00247D]"
              placeholder={"5,Marcus Johnson\n12,Chris Williams\n23,David Lee"}
              data-testid={`bulk-paste-area-${teamKey}`}
            />
            <Button size="sm" className="mt-2 bg-[#00247D] hover:bg-[#001A5C] text-white" onClick={handleBulkAdd} data-testid={`bulk-paste-add-${teamKey}`}>
              <Plus className="w-3 h-3 mr-1" /> Add All Players
            </Button>
          </div>
        )}

        {/* Player list */}
        <div className="space-y-2">
          {team.players.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                placeholder="#"
                value={p.jersey_number}
                onChange={(e) => onUpdatePlayer(i, 'jersey_number', e.target.value)}
                className="w-16 text-center font-mono"
                data-testid={`team-${teamKey}-jersey-${i}`}
              />
              <Input
                placeholder={`Player ${i + 1}`}
                value={p.name}
                onChange={(e) => onUpdatePlayer(i, 'name', e.target.value)}
                className="flex-1"
                data-testid={`team-${teamKey}-player-${i}`}
              />
              {team.players.length > 1 && (
                <Button variant="ghost" size="sm" onClick={() => onRemovePlayer(i)} className="text-slate-400 hover:text-red-500">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={onAddPlayer} className="mt-3 w-full" data-testid={`add-player-${teamKey}`}>
          <Plus className="w-4 h-4 mr-1" /> Add Player
        </Button>
      </CardContent>
    </Card>
  );
}

export default function StatsSetup() {
  const navigate = useNavigate();
  const [gameName, setGameName] = useState('');
  const [teamA, setTeamA] = useState({ name: '', players: [{ name: '', jersey_number: '' }] });
  const [teamB, setTeamB] = useState({ name: '', players: [{ name: '', jersey_number: '' }] });
  const [creating, setCreating] = useState(false);
  const [savedTeams, setSavedTeams] = useState([]);
  const [showSavedPicker, setShowSavedPicker] = useState(null);
  const [leagueEvents, setLeagueEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedMatchId, setSelectedMatchId] = useState('');
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [scanCreateOpen, setScanCreateOpen] = useState(false);
  // Game clock — configurable per game. Defaults to 10:00.
  const [quarterLengthMin, setQuarterLengthMin] = useState(10);
  // Filter text for the "Load Saved Team" modal — narrows the visible saved
  // teams as the user types.
  const [savedTeamSearch, setSavedTeamSearch] = useState('');

  // Team names that belong to the currently selected league event. When set,
  // the saved-team picker will hide teams that aren't part of this league so
  // the organizer sees only their own event's rosters. Empty set === no
  // league filter.
  const leagueTeamNames = useMemo(() => {
    if (!selectedEventId) return null;
    const ev = leagueEvents.find(e => e.id === selectedEventId);
    if (!ev) return null;
    const names = new Set();
    (ev.matches || []).forEach(m => {
      if (m.team_a_name) names.add(m.team_a_name.trim().toLowerCase());
      if (m.team_b_name) names.add(m.team_b_name.trim().toLowerCase());
    });
    return names.size > 0 ? names : null;
  }, [selectedEventId, leagueEvents]);

  // Applies both the search filter and the league-scoping filter. Kept in
  // useMemo so scrolling the modal doesn't re-run the string compare per row.
  const visibleSavedTeams = useMemo(() => {
    const q = savedTeamSearch.trim().toLowerCase();
    return savedTeams.filter(t => {
      if (q && !(t.name || '').toLowerCase().includes(q)) return false;
      if (leagueTeamNames && !leagueTeamNames.has((t.name || '').trim().toLowerCase())) return false;
      return true;
    });
  }, [savedTeams, savedTeamSearch, leagueTeamNames]);

  // Reset search whenever the modal opens/closes so the next open starts fresh.
  useEffect(() => {
    if (!showSavedPicker) setSavedTeamSearch('');
  }, [showSavedPicker]);

  useEffect(() => {
    fetchSavedTeams();
    fetchLeagueEvents();
    fetchSeasons();
  }, []);

  const fetchSavedTeams = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/stats/teams`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setSavedTeams(await res.json());
    } catch {}
  };

  const fetchLeagueEvents = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/stats/league-events`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setLeagueEvents(await res.json());
    } catch {}
  };

  const fetchSeasons = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/stats/seasons`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const list = await res.json();
        setSeasons(list);
        // Auto-select the most recently created season so any game loaded in
        // prep for live stats is tagged into the "current" season by default.
        // The /seasons endpoint returns records sorted by created_at desc, so
        // list[0] is always the newest. Respects a manual pick (falsy check).
        if (list.length > 0) {
          setSelectedSeasonId(prev => prev || list[0].id);
        }
      }
    } catch {}
  };

  // Resolve a saved roster from `savedTeams` for a given league match side.
  // Prefers an exact team-roster match (source==='team_roster' + same
  // event_id/team_id) so organizer-registered rosters win over ad-hoc
  // saved teams that may share a name. Falls back to case-insensitive
  // name match when no exact roster is available.
  const findSavedRosterForMatchSide = (eventId, teamId, teamName) => {
    // 1) Exact league roster: same event_id + team_id (deterministic sync)
    if (eventId && teamId) {
      const exact = savedTeams.find(
        t => t.source === 'team_roster' && t.event_id === eventId && t.team_id === teamId
      );
      if (exact) return exact;
    }
    // 2) Name match (case-insensitive), preferring team_roster source
    const target = (teamName || '').trim().toLowerCase();
    if (!target) return null;
    const nameMatches = savedTeams.filter(
      t => (t.name || '').trim().toLowerCase() === target
    );
    if (nameMatches.length === 0) return null;
    return nameMatches.find(t => t.source === 'team_roster') || nameMatches[0];
  };

  // Resolve the season whose start/end range contains the given ISO date.
  // Used to auto-suggest a season when the organizer picks a league match —
  // saves them a click and prevents "orphan" games missing from the season
  // leaderboard. Returns the season id or '' if no season covers the date.
  const findSeasonForDate = (isoDate) => {
    if (!isoDate || seasons.length === 0) return '';
    // Sort by start_date descending so the most-recent covering season wins
    // when ranges overlap.
    const sorted = [...seasons].sort((a, b) =>
      (b.start_date || '').localeCompare(a.start_date || '')
    );
    for (const s of sorted) {
      const start = s.start_date || '';
      const end = s.end_date || '';
      if (start && end && isoDate >= start && isoDate <= end) {
        return s.id;
      }
    }
    return '';
  };

  const handleLeagueMatchSelect = (matchId) => {
    setSelectedMatchId(matchId);
    if (!matchId) return;
    // Find the match in league events and pre-fill team names + game name.
    // If a saved roster exists for either side, auto-populate the players so
    // the organizer doesn't have to click "Load Saved" for each team.
    for (const event of leagueEvents) {
      const match = event.matches.find(m => m.id === matchId);
      if (match) {
        setSelectedEventId(event.id);
        setGameName(`${match.team_a_name} vs ${match.team_b_name}`);

        const homeRoster = findSavedRosterForMatchSide(
          event.id, match.team_a, match.team_a_name
        );
        const awayRoster = findSavedRosterForMatchSide(
          event.id, match.team_b, match.team_b_name
        );

        if (homeRoster) {
          setTeamA({
            name: match.team_a_name,
            players: homeRoster.players.map(p => ({
              name: p.name,
              jersey_number: p.jersey_number,
            })),
          });
        } else {
          setTeamA(prev => ({ ...prev, name: match.team_a_name }));
        }

        if (awayRoster) {
          setTeamB({
            name: match.team_b_name,
            players: awayRoster.players.map(p => ({
              name: p.name,
              jersey_number: p.jersey_number,
            })),
          });
        } else {
          setTeamB(prev => ({ ...prev, name: match.team_b_name }));
        }

        const loadedCount = (homeRoster ? 1 : 0) + (awayRoster ? 1 : 0);

        // Auto-suggest season when the match's date (or the event's start
        // date) falls within a season's range. Only overwrites when the
        // organizer hasn't manually chosen one — respects their pick.
        const seasonSourceDate = match.date || event.start_date || '';
        const suggestedSeason = findSeasonForDate(seasonSourceDate);
        if (suggestedSeason && !selectedSeasonId) {
          setSelectedSeasonId(suggestedSeason);
        }

        if (loadedCount === 2) {
          toast.success(`Loaded "${event.name}" — both rosters auto-loaded`);
        } else if (loadedCount === 1) {
          const loadedName = homeRoster ? match.team_a_name : match.team_b_name;
          const missingName = homeRoster ? match.team_b_name : match.team_a_name;
          toast.success(
            `Loaded "${event.name}" — ${loadedName} roster ready. Add players for ${missingName}.`
          );
        } else {
          toast.success(`Loaded match from "${event.name}"`);
        }
        break;
      }
    }
  };

  const addPlayer = (teamKey) => {
    const setter = teamKey === 'a' ? setTeamA : setTeamB;
    setter(prev => ({ ...prev, players: [...prev.players, { name: '', jersey_number: '' }] }));
  };

  const removePlayer = (teamKey, idx) => {
    const setter = teamKey === 'a' ? setTeamA : setTeamB;
    setter(prev => ({ ...prev, players: prev.players.filter((_, i) => i !== idx) }));
  };

  const updatePlayer = (teamKey, idx, field, value) => {
    const setter = teamKey === 'a' ? setTeamA : setTeamB;
    setter(prev => ({
      ...prev,
      players: prev.players.map((p, i) => i === idx ? { ...p, [field]: value } : p)
    }));
  };

  const handleCSVImport = (teamKey, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const players = parseCSV(ev.target.result);
      if (players.length === 0) { toast.error('No players found in CSV'); return; }
      const setter = teamKey === 'a' ? setTeamA : setTeamB;
      setter(prev => ({ ...prev, players }));
      toast.success(`Imported ${players.length} players`);
    };
    reader.readAsText(file);
  };

  const handleDeleteSavedTeam = async (saved, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete saved team "${saved.name}"? This cannot be undone — but doesn't affect any games already created with this team.`)) return;
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/stats/teams/${saved.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(`"${saved.name}" deleted`);
        setSavedTeams(prev => prev.filter(t => t.id !== saved.id));
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete');
      }
    } catch { toast.error('Network error'); }
  };

  const loadSavedTeam = (teamKey, saved) => {
    const setter = teamKey === 'a' ? setTeamA : setTeamB;
    setter({ name: saved.name, players: saved.players.map(p => ({ name: p.name, jersey_number: p.jersey_number })) });
    setShowSavedPicker(null);
    toast.success(`Loaded "${saved.name}"`);
  };

  const saveTeamRoster = async (teamKey) => {
    const team = teamKey === 'a' ? teamA : teamB;
    if (!team.name.trim()) { toast.error('Enter a team name first'); return; }
    const validPlayers = team.players.filter(p => p.name.trim());
    if (validPlayers.length === 0) { toast.error('Add at least one player'); return; }
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/stats/teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: team.name, players: validPlayers })
      });
      if (res.ok) {
        toast.success(`"${team.name}" saved for future use`);
        fetchSavedTeams();
      } else { toast.error('Failed to save team'); }
    } catch { toast.error('Network error'); }
  };

  const handleCreate = async () => {
    if (!gameName.trim()) { toast.error('Enter a game name'); return; }
    if (!teamA.name.trim() || !teamB.name.trim()) { toast.error('Enter both team names'); return; }
    const aPlayers = teamA.players.filter(p => p.name.trim());
    const bPlayers = teamB.players.filter(p => p.name.trim());
    if (aPlayers.length === 0 || bPlayers.length === 0) { toast.error('Each team needs at least 1 player'); return; }

    setCreating(true);
    try {
      const token = localStorage.getItem('session_token');
      // Local date in user's timezone (avoids the UTC date-rollover bug at night)
      const today = new Date();
      const localDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const res = await fetch(`${API_URL}/api/stats/games`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          name: gameName,
          date: localDate,
          team_a: { name: teamA.name, players: aPlayers },
          team_b: { name: teamB.name, players: bPlayers },
          event_id: selectedEventId || null,
          match_id: selectedMatchId || null,
          season_id: selectedSeasonId || null,
          quarter_length_sec: Math.max(1, Math.min(parseInt(quarterLengthMin, 10) || 10, 30)) * 60,
        })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Game created! Code: ${data.game.join_code}`);
        navigate(`/stats/game/${data.game.id}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create game');
      }
    } catch (e) { toast.error('Network error'); }
    finally { setCreating(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-effect sticky top-0 z-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 sm:gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/stats')} className="px-2 sm:px-3 shrink-0"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="text-base sm:text-xl font-bold text-[#00247D] truncate" style={{ fontFamily: 'Barlow Condensed' }}>
              <span className="hidden sm:inline">NEW GAME SETUP</span>
              <span className="sm:hidden">NEW GAME</span>
            </h1>
          </div>
          <Button
            onClick={() => setScanCreateOpen(true)}
            size="sm"
            className="bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-black font-semibold shadow-sm shrink-0 px-2.5 sm:px-3"
            data-testid="scan-create-open">
            <Sparkles className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Import Box Score</span>
            <span className="sm:hidden">Import</span>
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
        {/* Quick AI Scan banner */}
        <Card className="mb-6 border-amber-200 bg-gradient-to-r from-amber-50 to-amber-100/40">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-400/20 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-[#00247D]">Already have the box score? Import it here.</h3>
              <p className="text-xs text-slate-600 leading-relaxed">Photos, PDFs, or CSVs — skip manual roster entry and let us build the game and import every player's stats in seconds.</p>
            </div>
            <Button
              size="sm"
              onClick={() => setScanCreateOpen(true)}
              className="bg-[#00247D] hover:bg-[#001A5C] text-white shrink-0"
              data-testid="scan-create-banner-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Import Box Score
            </Button>
          </CardContent>
        </Card>
        {/* From League Section */}
        {leagueEvents.length > 0 && (
          <Card className="mb-6 border-amber-200 bg-amber-50/30">
            <CardContent className="p-4">
              <h3 className="text-sm font-bold text-[#00247D] uppercase mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                <FolderOpen className="w-4 h-4 inline mr-1" /> Link to League Match
              </h3>
              <div className="flex flex-wrap gap-3">
                <select
                  value={selectedEventId}
                  onChange={(e) => { setSelectedEventId(e.target.value); setSelectedMatchId(''); }}
                  className="border border-slate-200 rounded-md px-3 py-2 text-sm flex-1 min-w-[200px]"
                  data-testid="league-event-select">
                  <option value="">Select a league...</option>
                  {leagueEvents.map(ev => {
                    // Only count matches that don't already have a completed
                    // stats game so the "(N matches)" hint stays accurate.
                    const trackable = (ev.matches || []).filter(m => m.linked_game_status !== 'completed').length;
                    return (
                      <option key={ev.id} value={ev.id}>{ev.name} ({trackable} matches)</option>
                    );
                  })}
                </select>
                {selectedEventId && (
                  <select
                    value={selectedMatchId}
                    onChange={(e) => handleLeagueMatchSelect(e.target.value)}
                    className="border border-slate-200 rounded-md px-3 py-2 text-sm flex-1 min-w-[200px]"
                    data-testid="league-match-select">
                    <option value="">Select a match...</option>
                    {leagueEvents.find(ev => ev.id === selectedEventId)?.matches
                      .filter(m => m.linked_game_status !== 'completed')
                      .map(m => (
                        <option key={m.id} value={m.id}>
                          {m.team_a_name} vs {m.team_b_name} {m.stage ? `(${m.stage})` : ''}
                          {m.linked_game_status === 'active' ? ' — in progress' : ''}
                        </option>
                      ))}
                  </select>
                )}
              </div>
              {selectedMatchId && (
                <p className="text-xs text-green-600 mt-2">Stats for this game will be linked to the league schedule.</p>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex flex-wrap gap-6 mb-6">
          <div className="flex-1 min-w-[200px]">
            <label className="text-sm font-medium text-slate-600 block mb-1">Game Name</label>
            <Input
              placeholder="e.g. Varsity vs JV - Jan 15"
              value={gameName}
              onChange={(e) => setGameName(e.target.value)}
              data-testid="game-name-input"
            />
          </div>
          {seasons.length > 0 && (
            <div className="min-w-[180px]">
              <label className="text-sm font-medium text-slate-600 block mb-1">Season</label>
              <select value={selectedSeasonId} onChange={e => setSelectedSeasonId(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm h-10"
                data-testid="season-select">
                <option value="">No Season</option>
                {seasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          <div className="min-w-[140px]">
            <label className="text-sm font-medium text-slate-600 block mb-1">Quarter Length</label>
            <select value={quarterLengthMin} onChange={e => setQuarterLengthMin(parseInt(e.target.value, 10))}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm h-10"
              data-testid="quarter-length-select">
              <option value={8}>8:00 — High School / Rec</option>
              <option value={10}>10:00 — FIBA / College</option>
              <option value={12}>12:00 — NBA</option>
              <option value={15}>15:00</option>
              <option value={20}>20:00 — Halves</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <TeamForm
            team={teamA}
            onUpdateTeam={setTeamA}
            onAddPlayer={() => addPlayer('a')}
            onRemovePlayer={(idx) => removePlayer('a', idx)}
            onUpdatePlayer={(idx, field, val) => updatePlayer('a', idx, field, val)}
            onCSVImport={(e) => handleCSVImport('a', e)}
            onSave={() => saveTeamRoster('a')}
            onLoadSaved={() => setShowSavedPicker('a')}
            hasSavedTeams={savedTeams.length > 0}
            label="Home Team"
            teamKey="a"
          />
          <TeamForm
            team={teamB}
            onUpdateTeam={setTeamB}
            onAddPlayer={() => addPlayer('b')}
            onRemovePlayer={(idx) => removePlayer('b', idx)}
            onUpdatePlayer={(idx, field, val) => updatePlayer('b', idx, field, val)}
            onCSVImport={(e) => handleCSVImport('b', e)}
            onSave={() => saveTeamRoster('b')}
            onLoadSaved={() => setShowSavedPicker('b')}
            hasSavedTeams={savedTeams.length > 0}
            label="Away Team"
            teamKey="b"
          />
        </div>

        <div className="flex justify-end">
          <Button onClick={handleCreate} disabled={creating} className="bg-[#00247D] hover:bg-[#001A5C] text-white h-12 px-8 text-base font-bold" data-testid="start-game-btn">
            {creating ? 'Creating...' : 'Start Game'} <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>

      {/* Saved Teams Picker Modal */}
      {showSavedPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSavedPicker(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="saved-teams-modal">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>LOAD SAVED TEAM</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowSavedPicker(null)}><X className="w-4 h-4" /></Button>
            </div>
            {/* Search + optional "filtered by league" hint */}
            <div className="px-4 pt-4 pb-2 space-y-2 border-b border-slate-100">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  autoFocus
                  value={savedTeamSearch}
                  onChange={(e) => setSavedTeamSearch(e.target.value)}
                  placeholder="Search saved teams by name…"
                  className="pl-9 h-10"
                  data-testid="saved-teams-search-input"
                />
              </div>
              {leagueTeamNames && (
                <div className="flex items-center justify-between text-[11px] text-slate-500" data-testid="saved-teams-league-scope">
                  <span>Only showing teams from the selected league.</span>
                  <button
                    type="button"
                    onClick={() => { setSelectedEventId(''); setSelectedMatchId(''); }}
                    className="text-[#00247D] hover:underline font-medium"
                    data-testid="saved-teams-clear-league-filter"
                  >
                    Show all
                  </button>
                </div>
              )}
            </div>
            <div className="p-4 max-h-[400px] overflow-y-auto space-y-2">
              {savedTeams.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No saved teams yet</p>
              ) : visibleSavedTeams.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4" data-testid="saved-teams-empty-filtered">
                  {savedTeamSearch
                    ? `No saved teams match "${savedTeamSearch}"`
                    : 'None of your saved teams belong to the selected league yet.'}
                </p>
              ) : visibleSavedTeams.map(t => (
                <div key={t.id} className="flex items-stretch gap-1 group" data-testid={`saved-team-row-${t.id}`}>
                  <button
                    className="flex-1 text-left p-3 rounded-lg border border-slate-200 hover:border-[#00247D] hover:bg-blue-50/50 transition-colors"
                    onClick={() => loadSavedTeam(showSavedPicker, t)}
                    data-testid={`saved-team-${t.id}`}>
                    <div className="font-semibold text-slate-800 flex items-center gap-2">
                      {t.name}
                      {t.source === 'team_roster' && (
                        <span className="text-[10px] uppercase font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Roster</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {t.players.length} player{t.players.length !== 1 ? 's' : ''}: {t.players.slice(0, 4).map(p => `#${p.jersey_number} ${p.name}`).join(', ')}{t.players.length > 4 ? '...' : ''}
                    </div>
                  </button>
                  <button
                    onClick={(e) => handleDeleteSavedTeam(t, e)}
                    className="px-2 rounded-lg border border-slate-200 text-slate-400 hover:text-rose-600 hover:border-rose-200 hover:bg-rose-50 transition-colors"
                    title="Delete saved team"
                    data-testid={`delete-saved-team-${t.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <ScanCreateGameDialog
        open={scanCreateOpen}
        onOpenChange={setScanCreateOpen}
        initialName={gameName}
        initialHomeName={teamA.name}
        initialAwayName={teamB.name}
        eventId={selectedEventId}
        matchId={selectedMatchId}
        seasonId={selectedSeasonId}
        onCreated={(gameId) => navigate(`/stats/summary/${gameId}`)}
      />
    </div>
  );
}
